import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserCentricsService } from '@warema/shared/consent';
import { Dictionary } from '@warema/shared/models';
import { first, fromEvent } from 'rxjs';

interface GtmWindow extends Window {
    dataLayer: [unknown] | undefined;
}

declare let window: GtmWindow;

export interface GoogleTagManagerBaseObject {
    [key: string]: string | number | Date | Dictionary<unknown> | undefined;
}
export type GoogleTagManagerEventObject = GoogleTagManagerBaseObject & {
    event: string;
};

@Injectable({
    providedIn: 'root',
})
export class GoogleTrackingService {
    private readonly _userCentrics = inject(UserCentricsService);
    private readonly _destroyRef = inject(DestroyRef);

    private _dataLayerLoaded = false;
    private _dataLayerLoadPromise: Promise<void> | null = null;

    private readonly _tagManagerConsentGiven$ = this._userCentrics.hasUserConsented('Google Tag Manager');
    private _tagManagerConsentPromise: Promise<void> | null = null;

    constructor() {
        if (this._isDataLayerLoaded()) {
            this._dataLayerLoaded = true;
        } else {
            // eslint-disable-next-line sonarjs/no-async-constructor
            this._dataLayerLoadPromise = this._waitForDataLayerToLoad();
        }
    }

    public track(value: GoogleTagManagerEventObject): void {
        this._trackInternal(value);
    }

    public trackWithoutEvent(value: GoogleTagManagerBaseObject): void {
        this._trackInternal(value);
    }

    private _trackInternal(value: GoogleTagManagerEventObject | GoogleTagManagerBaseObject): void {
        if (this._dataLayerLoaded) {
            if (this._tagManagerConsentGiven$.getValue()) {
                this._pushToDataLayer(value);
            } else {
                this._tagManagerConsentPromise ??= this._waitForGtmConsentToBeTrue();
                void this._tagManagerConsentPromise
                    ?.then(() => this._pushToDataLayer(value))
                    .catch(e => console.error('Error while waiting for Google Tag Manager consent', e))
                    .finally(() => (this._tagManagerConsentPromise = null));
            }
        } else {
            this._dataLayerLoadPromise ??= this._waitForDataLayerToLoad();
            void this._dataLayerLoadPromise
                ?.then(() => this._trackInternal(value))
                .catch(e => console.error('Error while waiting for Google Tag Manager dataLayer to load', e))
                .finally(() => (this._dataLayerLoadPromise = null));
        }
    }

    private _pushToDataLayer(value: GoogleTagManagerEventObject | GoogleTagManagerBaseObject): void {
        window.dataLayer.push(value);
    }

    private _isDataLayerLoaded(): boolean {
        return window.dataLayer !== undefined && window.dataLayer.push !== undefined;
    }

    private _waitForGtmConsentToBeTrue(): Promise<void> {
        return new Promise((resolve, reject) => {
            this._tagManagerConsentGiven$
                .pipe(
                    first(x => x),
                    takeUntilDestroyed(this._destroyRef),
                )
                .subscribe({
                    next: () => resolve(),
                    error: e => reject(e),
                });
        });
    }

    private _waitForDataLayerToLoad(): Promise<void> {
        return new Promise((resolve, reject) => {
            fromEvent(window, 'datalayer.created')
                .pipe(first(), takeUntilDestroyed(this._destroyRef))
                .subscribe({
                    next: () => {
                        this._dataLayerLoaded = true;
                        resolve();
                    },
                    error: e => reject(e),
                });
        });
    }
}
